import { createContext } from 'preact';
import { useContext, useCallback, useMemo } from 'preact/hooks';
import { html } from '@utils/rendering/html';
import { useCachedFetch } from '../hooks/use-cached-fetch';
import { TimeSpans } from '../../constants/utility/time-spans.constants';

const FeatureContext = createContext({});

export function FeatureProvider({ children }) {
    // Use cached fetch with optimized settings for features
    const {
        data: featureMap = {},
        isLoading,
        isError,
    } = useCachedFetch(
        '/features',
        {}, // No query params needed
        {
            staleTime: TimeSpans.FiveMinutes, // Keep features fresh for 5 minutes
            cacheTime: TimeSpans.TenMinutes, // Keep in cache for 10 minutes
            refetchInterval: TimeSpans.FiveMinutes, // Auto-refresh every 5 minutes
            refetchIntervalInBackground: true, // Continue refreshing when tab is inactive
            refetchOnWindowFocus: false, // Don't refetch on every window focus
            retry: 3, // Retry failed requests
            retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
        }
    );

    const useFeature = useCallback(
        (key, fallback) => {
            // Return fallback if still loading or if feature doesn't exist
            if (isLoading || isError) {
                return fallback;
            }
            return featureMap[key] !== undefined ? featureMap[key] : fallback;
        },
        [featureMap, isLoading, isError]
    );

    // Memoize context value to prevent unnecessary re-renders
    const contextValue = useMemo(
        () => ({
            map: featureMap,
            useFeature,
            isLoading,
            isError,
        }),
        [featureMap, useFeature, isLoading, isError]
    );

    return html`
        <${FeatureContext.Provider} value=${contextValue}>
            ${children}
        </${FeatureContext.Provider}>
    `;
}

export const useFeatureChecks = () => useContext(FeatureContext);
